import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { doc, getFirestore, runTransaction } from "firebase/firestore";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useAuthState, useClientesActions } from "../../../context/AppContext";
import { queryKeys } from "../../../queries/queryKeys";
import { formatDate2 } from "../../../utils/formatters";
import {
  construirActualizacionRutaConMovimiento,
  crearMovimientoRutaData,
  crearMovimientoRutaRef,
} from "../../../utils/rutaMovimientos";
import type {
  AbonoCliente,
  AbonoClienteRecord,
  AbonoModalProps,
  CalculoAbonoResult,
  HistorialPago,
  MovimientoRutaResult,
  OverpaymentModalState,
  UseAbonoModalReturn,
  UsuarioRuta,
  UsuarioRutaRecord,
} from "./types";

const ERROR_EDITAR_ABONO_MESSAGE =
  "Error al Editar Abono. (DEBE INGRESAR LA CANTIDAD TOTAL DE DINERO ABONADO DURANTE EL DIA)";
const ERROR_CLIENTE_NO_ENCONTRADO_MESSAGE = "No se encontro la informacion del cliente";
const ERROR_CLIENTE_INVALIDO_MESSAGE = "No se pudo procesar la informacion del cliente";
const ERROR_ABONO_SUPERA_DEUDA_MESSAGE =
  "El abono es mayor a la deuda restante del cliente. Ajusta el valor.";
const ERROR_RUTA_NO_ENCONTRADA_MESSAGE = "No se encontro la informacion de la ruta";
const HISTORIAL_PAGOS_LIMIT = 80;
const DESCRIPCION_ABONO_CLIENTE = "abono cliente";
const DESCRIPCION_ABONO_ADMIN = "abono (admin)";
const DESCRIPCION_ABONO_EDITADO_RESTA_ADMIN = "abono editado resta (admin)";
const DESCRIPCION_ABONO_EDITADO_SUMA_ADMIN = "abono editado suma (admin)";
const DESCRIPCION_ABONO_EDITADO_RESTA = "Abono Editado (resta)";
const DESCRIPCION_ABONO_EDITADO_SUMA = "Abono Editado (suma)";

class AbonoValidationError extends Error {
  overpaymentModal: OverpaymentModalState | null;

  constructor(message: string, overpaymentModal: OverpaymentModalState | null = null) {
    super(message);
    this.name = "AbonoValidationError";
    this.overpaymentModal = overpaymentModal;
  }
}

const getFechaHoy = (): string => format(new Date(), "dd/MM/yyyy");

const getMillisecondsUntilNextDay = (): number => {
  const nextDay = new Date();
  nextDay.setHours(24, 0, 0, 0);

  return Math.max(nextDay.getTime() - Date.now(), 1_000);
};

const normalizarNumero = (value: number | string | null | undefined): number => {
  const parsedValue = Number(value ?? 0);

  return Number.isNaN(parsedValue) ? 0 : parsedValue;
};

const calcularCuotasAtrasadas = (
  cuotasAtrasadasActuales: number,
  cuotasPagadas: number,
): number => {
  const cuotasAtrasadasActualizadas =
    cuotasPagadas >= 2
      ? cuotasAtrasadasActuales - (cuotasPagadas - 1)
      : cuotasAtrasadasActuales - cuotasPagadas;

  return Math.max(0, cuotasAtrasadasActualizadas);
};

const calcularCuotasSuperadas = (monto: number, pagoDiario: number): number => {
  if (pagoDiario <= 0) {
    return 0;
  }

  return Math.floor(monto / pagoDiario);
};

const calcularSobrante = (monto: number, pagoDiario: number): number => {
  if (pagoDiario <= 0) {
    return 0;
  }

  return monto % pagoDiario;
};

const esMismoDia = (fechaA: Date, fechaB: Date): boolean => {
  return format(fechaA, "yyyy-MM-dd") === format(fechaB, "yyyy-MM-dd");
};

const normalizarFechaPago = (fecha: HistorialPago["fecha"]): Date => {
  if (fecha instanceof Date) {
    return fecha;
  }

  if (typeof fecha?.toDate === "function") {
    return fecha.toDate();
  }

  return new Date(fecha);
};

const limitarHistorialPagosRecientes = (
  historialPagos: HistorialPago[],
  limit = HISTORIAL_PAGOS_LIMIT,
): HistorialPago[] => {
  if (historialPagos.length <= limit) {
    return historialPagos;
  }

  return [...historialPagos]
    .sort(
      (pagoA, pagoB) =>
        normalizarFechaPago(pagoA.fecha).getTime() - normalizarFechaPago(pagoB.fecha).getTime(),
    )
    .slice(-limit);
};

const esPagoDelDia = (pago: HistorialPago, fechaActual: Date): boolean =>
  esMismoDia(normalizarFechaPago(pago.fecha), fechaActual);

const debeUsarFlujoEdicionAbono = (yaExistePagoHoy: boolean): boolean => yaExistePagoHoy;

const calcularDeudaTotal = (clienteData: AbonoClienteRecord): number => {
  return clienteData.cuotasPactadas * clienteData.pagoDiario;
};

const calcularDeudaRestanteActual = (clienteData: AbonoClienteRecord): number => {
  return Math.max(0, calcularDeudaTotal(clienteData) - clienteData.totalAbono);
};

const calcularMontoMaximoPermitido = (
  clienteData: AbonoClienteRecord,
  isEditingPayment: boolean,
): number => {
  const totalAbonoBase = isEditingPayment
    ? clienteData.totalAbono - clienteData.abono
    : clienteData.totalAbono;

  return Math.max(0, calcularDeudaTotal(clienteData) - totalAbonoBase);
};

const crearOverpaymentModalState = (
  clienteData: AbonoClienteRecord,
  isEditingPayment: boolean,
): OverpaymentModalState => {
  return {
    deudaRestante: calcularDeudaRestanteActual(clienteData),
    montoMaximoPermitido: calcularMontoMaximoPermitido(clienteData, isEditingPayment),
  };
};

const validarAbonoContraDeudaRestante = (
  clienteData: AbonoClienteRecord,
  abono: number,
  isEditingPayment: boolean,
): void => {
  const overpaymentModal = crearOverpaymentModalState(clienteData, isEditingPayment);

  if (abono > overpaymentModal.montoMaximoPermitido) {
    throw new AbonoValidationError(ERROR_ABONO_SUPERA_DEUDA_MESSAGE, overpaymentModal);
  }
};

const calcularResultadoAbono = (
  clienteData: AbonoClienteRecord,
  abono: number,
  isEditingPayment: boolean,
): CalculoAbonoResult => {
  const { cuotasAtrasadas, cuotasPagadas, pagoDiario } = clienteData;

  if (isEditingPayment) {
    if (clienteData.abono === abono) {
      return {
        cuotasAtrasadas: clienteData.cuotasAtrasadas,
        cuotasPagadas: clienteData.cuotasPagadas,
        errorMessage: ERROR_EDITAR_ABONO_MESSAGE,
        sobrante: clienteData.valorPico,
        totalAbono: clienteData.totalAbono,
      };
    }

    const vecesSuperadoAnterior = calcularCuotasSuperadas(clienteData.abono, pagoDiario);
    const vecesSuperadoActual = calcularCuotasSuperadas(abono, pagoDiario);

    if (clienteData.abono > abono) {
      const cuenta = vecesSuperadoAnterior - vecesSuperadoActual;
      const nuevasCuotasPagadas = Math.max(0, cuotasPagadas - cuenta);

      return {
        cuotasAtrasadas: calcularCuotasAtrasadas(cuotasAtrasadas, nuevasCuotasPagadas),
        cuotasPagadas: nuevasCuotasPagadas,
        sobrante: calcularSobrante(abono, pagoDiario),
        totalAbono: abono,
      };
    }

    const nuevasCuotasPagadas =
      clienteData.cuotasPagadas + (vecesSuperadoActual - vecesSuperadoAnterior);

    return {
      cuotasAtrasadas: calcularCuotasAtrasadas(cuotasAtrasadas, nuevasCuotasPagadas),
      cuotasPagadas: nuevasCuotasPagadas,
      sobrante: calcularSobrante(abono, pagoDiario),
      totalAbono: abono,
    };
  }

  const totalAbono = clienteData.valorPico + abono;
  const nuevasCuotasPagadas = cuotasPagadas + calcularCuotasSuperadas(totalAbono, pagoDiario);

  return {
    cuotasAtrasadas: calcularCuotasAtrasadas(cuotasAtrasadas, nuevasCuotasPagadas),
    cuotasPagadas: nuevasCuotasPagadas,
    sobrante: calcularSobrante(totalAbono, pagoDiario),
    totalAbono,
  };
};

const calcularMovimientoRuta = (
  saldoActual: number,
  clienteData: AbonoClienteRecord,
  abono: number,
  isEditingPayment: boolean,
  isAdmin: boolean,
): MovimientoRutaResult => {
  if (!isEditingPayment) {
    return {
      descripcion: isAdmin ? DESCRIPCION_ABONO_ADMIN : DESCRIPCION_ABONO_CLIENTE,
      montoFinal: abono,
      saldoActualizado: saldoActual + abono,
      tipo: "ingreso",
    };
  }

  if (clienteData.abono > abono) {
    const diferencia = clienteData.abono - abono;

    return {
      descripcion: isAdmin
        ? DESCRIPCION_ABONO_EDITADO_RESTA_ADMIN
        : DESCRIPCION_ABONO_EDITADO_RESTA,
      montoFinal: diferencia,
      saldoActualizado: saldoActual - diferencia,
      tipo: "salida",
    };
  }

  const diferencia = abono - clienteData.abono;

  return {
    descripcion: isAdmin ? DESCRIPCION_ABONO_EDITADO_SUMA_ADMIN : DESCRIPCION_ABONO_EDITADO_SUMA,
    montoFinal: diferencia,
    saldoActualizado: saldoActual + diferencia,
    tipo: "ingreso",
  };
};

const construirHistorialPagos = (
  clienteData: AbonoClienteRecord,
  abono: number,
  observaciones: string,
  isEditingPayment: boolean,
  fechaActual: Date,
): HistorialPago[] => {
  const nuevoHistorial: HistorialPago = {
    abono,
    fecha: fechaActual,
    observaciones,
  };

  if (isEditingPayment) {
    const historialSinPagosDelDia = clienteData.historialPagos.filter(
      (historial) => !esPagoDelDia(historial, fechaActual),
    );

    return limitarHistorialPagosRecientes([...historialSinPagosDelDia, nuevoHistorial]);
  }

  return limitarHistorialPagosRecientes([...clienteData.historialPagos, nuevoHistorial]);
};

const calcularTotalAbonoFinal = (
  clienteData: AbonoClienteRecord,
  abono: number,
  isEditingPayment: boolean,
): number => {
  if (!isEditingPayment) {
    return clienteData.totalAbono + abono;
  }

  if (clienteData.abono > abono) {
    return clienteData.totalAbono - (clienteData.abono - abono);
  }

  return clienteData.totalAbono + (abono - clienteData.abono);
};

const normalizarCliente = (
  cliente: AbonoCliente | null | undefined,
  fallbackUid?: string,
): AbonoClienteRecord | null => {
  const uid = cliente?.uid ?? fallbackUid;

  if (!uid) {
    return null;
  }

  return {
    ...cliente,
    abono: normalizarNumero(cliente.abono),
    cuotasAtrasadas: normalizarNumero(cliente.cuotasAtrasadas),
    cuotasPagadas: normalizarNumero(cliente.cuotasPagadas),
    cuotasPactadas: normalizarNumero(cliente.cuotasPactadas),
    historialPagos: limitarHistorialPagosRecientes(cliente.historialPagos ?? []),
    nombreCliente: cliente.nombreCliente ?? "",
    pagoDiario: normalizarNumero(cliente.pagoDiario),
    totalAbono: normalizarNumero(cliente.totalAbono),
    uid,
    valorPico: normalizarNumero(cliente.valorPico),
  };
};

const normalizarUsuarioRuta = (usuarioRuta: UsuarioRuta): UsuarioRutaRecord => ({
  ...usuarioRuta,
  adminUid: usuarioRuta.adminUid ?? "",
  saldoInicial: usuarioRuta.saldoInicial ?? 0,
  uid: usuarioRuta.uid ?? "",
});

const debeAbrirRenovacion = (
  totalAbonoFinal: number,
  clienteActualizado: AbonoClienteRecord,
): boolean => {
  const deudaTotal = clienteActualizado.cuotasPactadas * clienteActualizado.pagoDiario;

  return (
    totalAbonoFinal === deudaTotal ||
    clienteActualizado.cuotasPagadas === clienteActualizado.cuotasPactadas
  );
};

type ResultadoTransaccionAbono = {
  clienteActualizado: AbonoClienteRecord;
  rutaActualizada: UsuarioRutaRecord;
  shouldOpenRenovacion: boolean;
};

export const useAbonoModal = ({
  selectedAbono,
  setIsAbono,
  setIsModalPreguntaRenovacion,
  setSelectedAbono,
  setUsuarioRuta,
  usuarioRuta,
}: AbonoModalProps): UseAbonoModalReturn => {
  const { userData } = useAuthState();
  const { setInfoClientes } = useClientesActions();
  const queryClient = useQueryClient();
  const [isSubmiting, setIsSubmiting] = useState(false);
  const [abono, setAbono] = useState(0);
  const [abonoInput, setAbonoInput] = useState("");
  const [observaciones, setObservaciones] = useState("Dinheiro");
  const [overpaymentModal, setOverpaymentModal] = useState<OverpaymentModalState | null>(null);
  const [fechaHoy, setFechaHoy] = useState(getFechaHoy);
  const submitLockRef = useRef(false);
  const isAdmin = Boolean(userData?.isAdmin);
  const selectedAbonoNormalizado = useMemo(() => normalizarCliente(selectedAbono), [selectedAbono]);
  const usuarioRutaNormalizada = useMemo(() => normalizarUsuarioRuta(usuarioRuta), [usuarioRuta]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const scheduleNextUpdate = () => {
      timeoutId = setTimeout(() => {
        setFechaHoy(getFechaHoy());
        scheduleNextUpdate();
      }, getMillisecondsUntilNextDay());
    };

    setFechaHoy(getFechaHoy());
    scheduleNextUpdate();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  const pagoHoy = useMemo(() => {
    if (!selectedAbonoNormalizado?.historialPagos.length) {
      return false;
    }

    return selectedAbonoNormalizado.historialPagos.some(
      (pago) => formatDate2(pago.fecha) === fechaHoy,
    );
  }, [fechaHoy, selectedAbonoNormalizado]);

  const shouldEditTodayPayment = useMemo(() => debeUsarFlujoEdicionAbono(pagoHoy), [pagoHoy]);
  const showPaymentInputs = Boolean(selectedAbonoNormalizado);
  const title = shouldEditTodayPayment ? "Editar abono diario" : "Ingresar abono diario";

  useEffect(() => {
    if (!selectedAbonoNormalizado) {
      setAbono(0);
      setAbonoInput("");
      return;
    }

    if (shouldEditTodayPayment && pagoHoy) {
      const montoActual = normalizarNumero(selectedAbonoNormalizado.abono);

      setAbono(montoActual);
      setAbonoInput(montoActual > 0 ? String(montoActual) : "");
      return;
    }

    const montoPagoDiario = normalizarNumero(selectedAbonoNormalizado.pagoDiario);

    setAbono(montoPagoDiario);
    setAbonoInput(montoPagoDiario > 0 ? String(montoPagoDiario) : "");
  }, [pagoHoy, selectedAbonoNormalizado, shouldEditTodayPayment]);

  const handleClose = useCallback(() => {
    setIsAbono(false);
  }, [setIsAbono]);

  const handleAbonoChange = useCallback((value: string) => {
    setAbonoInput(value);
    setOverpaymentModal(null);

    const parsedValue = parseInt(value, 10);
    setAbono(Number.isNaN(parsedValue) ? 0 : parsedValue);
  }, []);

  const handleObservacionesChange = useCallback((value: string) => {
    setObservaciones(value);
  }, []);

  const closeOverpaymentModal = useCallback(() => {
    setOverpaymentModal(null);
  }, []);

  const applyOverpaymentSuggestedAmount = useCallback(() => {
    if (!overpaymentModal) {
      return;
    }

    setAbono(overpaymentModal.montoMaximoPermitido);
    setAbonoInput(
      overpaymentModal.montoMaximoPermitido > 0
        ? String(overpaymentModal.montoMaximoPermitido)
        : "",
    );
    setOverpaymentModal(null);
  }, [overpaymentModal]);

  const handleAbonar = useCallback(async () => {
    if (submitLockRef.current || !selectedAbonoNormalizado) {
      return;
    }

    if (!usuarioRutaNormalizada.adminUid || !usuarioRutaNormalizada.uid) {
      toast.error(ERROR_RUTA_NO_ENCONTRADA_MESSAGE);
      return;
    }

    if (Number.isNaN(abono) || abono < 0) {
      toast.error("Ingrese un monto valido");
      return;
    }

    try {
      validarAbonoContraDeudaRestante(selectedAbonoNormalizado, abono, shouldEditTodayPayment);
    } catch (error) {
      if (error instanceof AbonoValidationError && error.overpaymentModal) {
        setOverpaymentModal(error.overpaymentModal);
        return;
      }

      throw error;
    }

    submitLockRef.current = true;

    try {
      setIsSubmiting(true);

      const db = getFirestore();
      const fechaActual = new Date();
      const rutaRef = doc(
        db,
        "admin_users",
        usuarioRutaNormalizada.adminUid,
        "rutas",
        usuarioRutaNormalizada.uid,
      );

      const clienteRef = doc(
        db,
        "admin_users",
        usuarioRutaNormalizada.adminUid,
        "rutas",
        usuarioRutaNormalizada.uid,
        "clientes",
        selectedAbonoNormalizado.uid,
      );
      const movimientoRef = crearMovimientoRutaRef(
        db,
        usuarioRutaNormalizada.adminUid,
        usuarioRutaNormalizada.uid,
      );

      const resultadoTransaccion = await runTransaction(
        db,
        async (transaction): Promise<ResultadoTransaccionAbono> => {
          const [rutaSnapshot, clienteSnapshot] = await Promise.all([
            transaction.get(rutaRef),
            transaction.get(clienteRef),
          ]);
          const rutaFirestoreData = rutaSnapshot.data() as UsuarioRuta | undefined;
          const clienteFirestoreData = clienteSnapshot.data() as AbonoCliente | undefined;

          if (!rutaSnapshot.exists() || !rutaFirestoreData) {
            throw new AbonoValidationError(ERROR_RUTA_NO_ENCONTRADA_MESSAGE);
          }

          if (!clienteSnapshot.exists() || !clienteFirestoreData) {
            throw new AbonoValidationError(ERROR_CLIENTE_NO_ENCONTRADO_MESSAGE);
          }

          const clienteData = normalizarCliente(clienteFirestoreData, selectedAbonoNormalizado.uid);

          if (!clienteData) {
            throw new AbonoValidationError(ERROR_CLIENTE_INVALIDO_MESSAGE);
          }

          const rutaData = normalizarUsuarioRuta({
            ...rutaFirestoreData,
            adminUid: usuarioRutaNormalizada.adminUid,
            uid: rutaSnapshot.id,
          });
          const pagoDelDiaExistente = clienteData.historialPagos.some((pago) =>
            esPagoDelDia(pago, fechaActual),
          );
          const shouldEditTransactionPayment = debeUsarFlujoEdicionAbono(pagoDelDiaExistente);
          const calculoAbono = calcularResultadoAbono(
            clienteData,
            abono,
            shouldEditTransactionPayment,
          );
          validarAbonoContraDeudaRestante(clienteData, abono, shouldEditTransactionPayment);

          if (calculoAbono.errorMessage) {
            throw new AbonoValidationError(calculoAbono.errorMessage);
          }

          const movimientoRuta = calcularMovimientoRuta(
            normalizarNumero(rutaData.saldoInicial),
            clienteData,
            abono,
            shouldEditTransactionPayment,
            isAdmin,
          );
          const saldoRutaActualizado = movimientoRuta.saldoActualizado;
          const movimiento = crearMovimientoRutaData({
            clienteUid: clienteData.uid,
            descripcion: movimientoRuta.descripcion,
            fecha: fechaActual,
            monto: movimientoRuta.montoFinal,
            origen: shouldEditTransactionPayment ? "abono_editado" : "abono",
            responsable: clienteData.nombreCliente,
            saldoDespues: saldoRutaActualizado,
            tipo: movimientoRuta.tipo,
          });
          const rutaUpdates = construirActualizacionRutaConMovimiento(
            rutaData,
            movimiento,
            saldoRutaActualizado,
          );
          const historialPagosActualizado = construirHistorialPagos(
            clienteData,
            abono,
            observaciones,
            shouldEditTransactionPayment,
            fechaActual,
          );
          const totalAbonoFinal = calcularTotalAbonoFinal(
            clienteData,
            abono,
            shouldEditTransactionPayment,
          );
          const fechaUltimoAbono = fechaActual.toDateString();

          const clienteActualizado: AbonoClienteRecord = {
            ...clienteData,
            abono,
            cuotasAtrasadas: calculoAbono.cuotasAtrasadas,
            cuotasPagadas: calculoAbono.cuotasPagadas,
            fechaUltimoAbono,
            historialPagos: historialPagosActualizado,
            totalAbono: totalAbonoFinal,
            valorPico: calculoAbono.sobrante,
          };
          const rutaActualizada: UsuarioRutaRecord = {
            ...rutaData,
            ...rutaUpdates,
          };

          transaction.set(movimientoRef, movimiento);
          transaction.update(rutaRef, {
            ...rutaUpdates,
          });
          transaction.update(clienteRef, {
            abono,
            cuotasAtrasadas: calculoAbono.cuotasAtrasadas,
            cuotasPagadas: calculoAbono.cuotasPagadas,
            fechaUltimoAbono,
            historialPagos: historialPagosActualizado,
            totalAbono: totalAbonoFinal,
            valorPico: calculoAbono.sobrante,
          });

          return {
            clienteActualizado,
            rutaActualizada,
            shouldOpenRenovacion:
              !isAdmin && debeAbrirRenovacion(totalAbonoFinal, clienteActualizado),
          };
        },
      );

      setUsuarioRuta(resultadoTransaccion.rutaActualizada);
      setSelectedAbono(resultadoTransaccion.clienteActualizado);
      setInfoClientes(
        (previousClientes) =>
          previousClientes?.map((cliente) =>
            cliente.uid === resultadoTransaccion.clienteActualizado.uid
              ? { ...cliente, ...resultadoTransaccion.clienteActualizado }
              : cliente,
          ) ?? previousClientes,
      );

      // El abono escribe un movimiento nuevo; invalidamos las vistas que lo
      // muestran (historial paginado y metricas del dia) para que no queden
      // desactualizadas mientras el staleTime no haya expirado.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.movimientos(
          usuarioRutaNormalizada.adminUid,
          usuarioRutaNormalizada.uid,
        ),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.movimientosDelDiaByRuta(
          usuarioRutaNormalizada.adminUid,
          usuarioRutaNormalizada.uid,
        ),
      });

      if (resultadoTransaccion.shouldOpenRenovacion) {
        setIsAbono(false);
        setIsModalPreguntaRenovacion(true);
        return;
      }

      setIsAbono(false);
    } catch (error) {
      if (error instanceof AbonoValidationError) {
        if (error.overpaymentModal) {
          setOverpaymentModal(error.overpaymentModal);
          return;
        }

        toast.error(error.message);
        return;
      }

      console.error("Error updating data in Firebase:", error);
      toast.error("No se pudo registrar el abono");
    } finally {
      submitLockRef.current = false;
      setIsSubmiting(false);
    }
  }, [
    abono,
    isAdmin,
    observaciones,
    queryClient,
    selectedAbonoNormalizado,
    setIsAbono,
    setIsModalPreguntaRenovacion,
    setSelectedAbono,
    setInfoClientes,
    setUsuarioRuta,
    shouldEditTodayPayment,
    usuarioRutaNormalizada,
  ]);

  return {
    abonoInput,
    applyOverpaymentSuggestedAmount,
    closeOverpaymentModal,
    handleAbonar,
    handleAbonoChange,
    handleClose,
    handleObservacionesChange,
    isSubmiting,
    observaciones,
    overpaymentModal,
    pagoHoy,
    showPaymentInputs,
    title,
  };
};
